import { API_ENDPOINTS, apiClient, withErrorHandling } from '../api/index.js';
import { encryptPassword } from '../utils/crypto.js';
import { generateDeviceIdWithTimestamp } from '../utils/deviceHelper.js';

class AuthService {
  constructor() {
    this.apiClient = apiClient;
  }

  async sendEmailVerification(email, recaptchaToken, purpose = 'sign_up') {
    return await withErrorHandling(
      async () => {
        const response = await this.apiClient.post(API_ENDPOINTS.auth.emailVerification, {
          email,
          purpose,
          recaptchaToken
        });
        return response;
      },
      { operation: 'sendEmailVerification', email, purpose }
    );
  }

  async validateOTP(email, otp, token, purpose = 'sign_up') {
    return await withErrorHandling(
      async () => {
        const response = await this.apiClient.post(API_ENDPOINTS.auth.validateOTP, {
          email,
          otp,
          token,
          purpose
        });
        return response;
      },
      { operation: 'validateOTP', email, purpose }
    );
  }

  async setPassword(email, password, confirmPassword, token = null) {
    if (password !== confirmPassword) {
      return {
        success: false,
        error: 'Passwords do not match'
      };
    }
    return await withErrorHandling(
      async () => {
        const encryptedPassword = await encryptPassword(password);
        const deviceId = generateDeviceIdWithTimestamp();
        const payload = {
          email,
          password: encryptedPassword,
          confirmPassword: encryptedPassword,
          device: deviceId
        };
        if (token) {
          payload.token = token;
        }
        const response = await this.apiClient.post(API_ENDPOINTS.auth.setPassword, payload);
        if (response.data?.token) {
          console.log('🔐 JWT token received from setPassword');
          await this.apiClient.setAuthToken(response.data.token);
          const { useAuthStore } = await import('../stores/authStore.js');
          const setAuthenticationState = useAuthStore.getState().setAuthenticationState;
          const userData = {
            id: response.data?.userId,
            email: response.data?.userEmailId || email,
            token: response.data.token,
            ...response.data
          };
          response.deviceId = deviceId;
          console.log('🔐 Setting authentication state after setPassword:', { ...userData, deviceId });
          setAuthenticationState({ ...userData, deviceId });
        } else {
          console.warn('⚠️ No JWT token in setPassword response');
        }
        return response;
      },
      { operation: 'setPassword', email, hasToken: !!token }
    );
  }

  async setProfile(branchId, semesterId, universityId) {
    return await withErrorHandling(
      async () => {
        const payload = {
          branchId,
          semesterId,
          universityId
        };
        console.log('📤 Set Profile API Call:', { endpoint: API_ENDPOINTS.auth.signup, payload });
        const response = await this.apiClient.post(API_ENDPOINTS.auth.signup, payload);
        console.log('📥 Set Profile API Response:', response);
        return response;
      },
      { operation: 'setProfile' }
    );
  }

  async signup(branchId, semesterId, universityId) {
    return await withErrorHandling(
      async () => {
        const payload = {
          branchId,
          semesterId,
          universityId
        };
        console.log('📤 Signup API Call:', { endpoint: API_ENDPOINTS.auth.signup, payload });
        const response = await this.apiClient.post(API_ENDPOINTS.auth.signup, payload);
        console.log('📥 Signup API Response:', response);
        return response;
      },
      { operation: 'signup' }
    );
  }

  async createAccount(email, password, confirmPassword) {
    if (password !== confirmPassword) {
      return {
        success: false,
        error: 'Passwords do not match'
      };
    }
    return await withErrorHandling(
      async () => {
        const encryptedPassword = await encryptPassword(password);
        const response = await this.apiClient.post(API_ENDPOINTS.auth.createAccount, {
          email,
          password: encryptedPassword,
          confirmPassword: encryptedPassword
        });
        return response;
      },
      { operation: 'createAccount', email }
    );
  }

  async signin(email, password, recaptchaToken) {
    // Check for existing device ID in localStorage, generate new one if not found
    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      deviceId = generateDeviceIdWithTimestamp();
      localStorage.setItem('deviceId', deviceId);
      console.log('🔐 Generated new device ID for signin:', deviceId);
    } else {
      console.log('🔐 Using existing device ID for signin:', deviceId);
    }
    const result = await withErrorHandling(
      async () => {
        const encryptedPassword = await encryptPassword(password);
        const response = await this.apiClient.post(API_ENDPOINTS.auth.signin, {
          email,
          password: encryptedPassword,
          recaptchaToken,
          device: deviceId,
          portalType: "WEBSITE"
        });
        if (response.data?.token) {
          await this.apiClient.setAuthToken(response.data.token);
        }
        response.deviceId = deviceId; // Attach the sent deviceId
        return response;
      },
      { operation: 'signin', email }
    );
    console.log('🔐 Added device ID to signin result:', { deviceId, resultSuccess: result.success });
    return result;
  }

  async selectUniversities() {
    const result = await withErrorHandling(async () => {
      const response = await this.apiClient.get(API_ENDPOINTS.user.selectUniversities);
      return response;
    });
    return result;
  }

  async selectBranches() {
    const result = await withErrorHandling(async () => {
      const response = await this.apiClient.get(API_ENDPOINTS.user.selectBranches);
      return response;
    });
    return result;
  }

  async selectSemesters() {
    return withErrorHandling(async () => {
      const response = await this.apiClient.get(API_ENDPOINTS.user.selectSemesters);
      return response;
    }, { operation: 'selectSemesters' });
  }

  async login(email, password, recaptchaToken) {
    const deviceId = generateDeviceIdWithTimestamp();
    const result = await withErrorHandling(
      async () => {
        const encryptedPassword = await encryptPassword(password);
        const response = await this.apiClient.post(API_ENDPOINTS.auth.login, {
          email,
          password: encryptedPassword,
          recaptchaToken,
          device: deviceId
        });
        if (response.token) {
          await this.apiClient.setAuthToken(response.token);
        }
        if (response.success) {
          response.deviceId = deviceId;
        }
        return response;
      },
      { operation: 'login', email }
    );
    return result;
  }

  async socialLogin(email, providerName, socialUserId, deviceType = "web", ipAddress = null, location = null, fcmToken = null, deviceId) {
    console.log('🔐 Using provided deviceId for socialLogin:', deviceId);
    const result = await withErrorHandling(
      async () => {
        const payload = {
          email,
          providerName,
          socialUserId,
          device: deviceId,
          portalType: "WEBSITE"
        };
        if (ipAddress) payload.ipAddress = ipAddress;
        if (location) payload.location = location;
        if (fcmToken) payload.fcmToken = fcmToken;
        console.log('🔐 Social Login Payload:', payload);
        const response = await this.apiClient.post(API_ENDPOINTS.auth.signin, payload);
        if (response.data?.token) {
          await this.apiClient.setAuthToken(response.data.token);
        }
        response.deviceId = deviceId; // Attach the sent deviceId
        console.log('🔍 Social Login Response:', { ...response, deviceId });
        return response;
      },
      { operation: 'socialLogin', email, providerName }
    );
    return result;
  }

  async logout() {
    const result = await withErrorHandling(
      async () => {
        const response = await this.apiClient.post(API_ENDPOINTS.auth.logout);
        return response;
      },
      { operation: 'logout' }
    );
    await this.apiClient.setAuthToken(null);
    if (!result.success) {
      return {
        success: true,
        data: { message: 'Logged out locally' }
      };
    }
    return result;
  }

  async signout(email, deviceId = null) {
    const result = await withErrorHandling(
      async () => {
        const finalDeviceId = deviceId || generateDeviceIdWithTimestamp();
        console.log('🔐 Signout device ID:', {
          provided: !!deviceId,
          deviceId: finalDeviceId,
          source: deviceId ? 'stored_from_signin' : 'newly_generated'
        });
        const queryParams = new URLSearchParams({
          email: email,
          device: finalDeviceId
        });
        console.log('📤 Signout API Call:', {
          endpoint: `${API_ENDPOINTS.auth.signout}?${queryParams.toString()}`,
          email,
          deviceId: finalDeviceId
        });
        const response = await this.apiClient.get(`${API_ENDPOINTS.auth.signout}?${queryParams.toString()}`);
        console.log('📥 Signout API Response:', response);
        return response;
      },
      { operation: 'signout', email }
    );
    return result;
  }

  async resetPassword(token, email, password, confirmPassword) {
    if (password !== confirmPassword) {
      return {
        success: false,
        error: 'Passwords do not match'
      };
    }
    return await withErrorHandling(
      async () => {
        const encryptedPassword = await encryptPassword(password);
        const payload = {
          token,
          email,
          password: encryptedPassword,
          confirmPassword: encryptedPassword
        };
        console.log('📤 Reset Password API Call:', {
          endpoint: API_ENDPOINTS.auth.resetPassword,
          payload: { ...payload, password: '[ENCRYPTED]', confirmPassword: '[ENCRYPTED]' }
        });
        const response = await this.apiClient.post(API_ENDPOINTS.auth.resetPassword, payload);
        console.log('📥 Reset Password API Response:', response);
        return response;
      },
      { operation: 'resetPassword', email }
    );
  }

  async resendEmailVerification(email, purpose = 'sign_up') {
    return await withErrorHandling(
      async () => {
        const response = await this.apiClient.post(API_ENDPOINTS.auth.resendVerification, {
          email,
          purpose
        });
        return response;
      },
      { operation: 'resendEmailVerification', email, purpose }
    );
  }
}

export default AuthService;